import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Plus, PencilSimple, Trash, Play, Stop, BookOpen } from '@phosphor-icons/react'
import { toast } from 'sonner'
import type { Mission, MissionStatus } from '@/types'

interface MissionsTabProps {
  missions: Mission[]
  isGameMaster: boolean
  onCreate: (data: { title: string; description: string }) => Promise<void>
  onUpdate: (missionId: string, data: { title: string; description: string }) => Promise<void>
  onReorder: (missionId: string, order: number) => Promise<void>
  onActivate: (missionId: string) => Promise<void>
  onClose: (missionId: string) => Promise<void>
  onDelete: (missionId: string) => Promise<void>
}

const STATUS_META: Record<MissionStatus, { label: string; variant: 'secondary' | 'default' | 'outline' }> = {
  rascunho: { label: 'Rascunho', variant: 'outline' },
  ativa: { label: 'Ativa', variant: 'default' },
  encerrada: { label: 'Encerrada', variant: 'secondary' },
}

export function MissionsTab({
  missions,
  isGameMaster,
  onCreate,
  onUpdate,
  onReorder,
  onActivate,
  onClose,
  onDelete,
}: MissionsTabProps) {
  const [showDialog, setShowDialog] = useState(false)
  const [editing, setEditing] = useState<Mission | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const ordered = [...missions].sort((a, b) => a.order - b.order)

  const openCreate = () => {
    setEditing(null)
    setTitle('')
    setDescription('')
    setShowDialog(true)
  }

  const openEdit = (mission: Mission) => {
    setEditing(mission)
    setTitle(mission.title)
    setDescription(mission.description)
    setShowDialog(true)
  }

  const handleSave = async () => {
    const trimmed = title.trim()
    if (!trimmed) {
      toast.error('A missão precisa de um título')
      return
    }
    setSaving(true)
    try {
      if (editing) {
        await onUpdate(editing.id, { title: trimmed, description })
        toast.success('Missão atualizada')
      } else {
        await onCreate({ title: trimmed, description })
        toast.success('Missão criada')
      }
      setShowDialog(false)
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao salvar missão')
    } finally {
      setSaving(false)
    }
  }

  const runAction = async (id: string, fn: () => Promise<void>, errMsg: string) => {
    setBusyId(id)
    try {
      await fn()
    } catch (error: any) {
      toast.error(error?.message || errMsg)
    } finally {
      setBusyId(null)
    }
  }

  const handleActivate = (m: Mission) =>
    runAction(m.id, async () => {
      await onActivate(m.id)
      toast.success(`Missão "${m.title}" ativada`)
    }, 'Erro ao ativar missão')

  const handleClose = (m: Mission) =>
    runAction(m.id, async () => {
      await onClose(m.id)
      toast.success(`Missão "${m.title}" encerrada`)
    }, 'Erro ao encerrar missão')

  const handleDelete = (m: Mission) => {
    if (!confirm(`Apagar o rascunho "${m.title}"? Esta ação não pode ser desfeita.`)) return
    return runAction(m.id, async () => {
      await onDelete(m.id)
      toast.success('Rascunho apagado')
    }, 'Erro ao apagar missão')
  }

  const handleMove = (m: Mission, direction: -1 | 1) => {
    const idx = ordered.findIndex((x) => x.id === m.id)
    const swapIdx = idx + direction
    if (swapIdx < 0 || swapIdx >= ordered.length) return
    const neighbor = ordered[swapIdx]
    // Troca as ordens dos dois vizinhos.
    return runAction(m.id, async () => {
      await onReorder(m.id, neighbor.order)
      await onReorder(neighbor.id, m.order)
    }, 'Erro ao reordenar')
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Missões da campanha</h3>
          <p className="text-sm text-muted-foreground">
            Capítulos da história. Só uma missão fica ativa por vez — é nela que o chat do jogo acontece.
          </p>
        </div>
        {isGameMaster && (
          <Button onClick={openCreate} className="shrink-0">
            <Plus size={16} className="mr-2" />
            Nova missão
          </Button>
        )}
      </div>

      {ordered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 rounded-full bg-muted p-4">
              <BookOpen size={32} className="text-muted-foreground" />
            </div>
            <h4 className="mb-2 text-lg font-medium">Nenhuma missão ainda</h4>
            <p className="mb-4 max-w-md text-muted-foreground">
              {isGameMaster
                ? 'Crie missões como capítulos da sua campanha. Você pode planejá-las com antecedência ou criar uma de cada vez.'
                : 'O mestre ainda não abriu nenhuma missão nesta campanha.'}
            </p>
            {isGameMaster && (
              <Button onClick={openCreate}>
                <Plus size={16} className="mr-2" />
                Criar primeira missão
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {ordered.map((mission, idx) => {
            const meta = STATUS_META[mission.status]
            const isBusy = busyId === mission.id
            return (
              <Card key={mission.id} className={mission.status === 'ativa' ? 'border-primary' : ''}>
                <CardHeader className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Missão {mission.order}
                        </span>
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </div>
                      <CardTitle className="mt-1 truncate text-lg">{mission.title}</CardTitle>
                      {mission.description && (
                        <CardDescription className="mt-1 line-clamp-2">{mission.description}</CardDescription>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">
                        {mission.message_count} mensage{mission.message_count === 1 ? 'm' : 'ns'}
                        {mission.sessions.length > 0 &&
                          ` · ${mission.sessions.length} sess${mission.sessions.length === 1 ? 'ão' : 'ões'}`}
                      </p>
                    </div>

                    {isGameMaster && (
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            aria-label="Mover para cima"
                            disabled={idx === 0 || isBusy}
                            onClick={() => handleMove(mission, -1)}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            aria-label="Mover para baixo"
                            disabled={idx === ordered.length - 1 || isBusy}
                            onClick={() => handleMove(mission, 1)}
                          >
                            ↓
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardHeader>

                {isGameMaster && (
                  <CardContent className="flex flex-wrap gap-2 px-4 pb-4 pt-0">
                    {mission.status === 'ativa' ? (
                      <Button size="sm" variant="outline" disabled={isBusy} onClick={() => handleClose(mission)}>
                        <Stop size={14} className="mr-1" />
                        Encerrar
                      </Button>
                    ) : (
                      <Button size="sm" disabled={isBusy} onClick={() => handleActivate(mission)}>
                        <Play size={14} className="mr-1" />
                        {mission.status === 'encerrada' ? 'Reativar' : 'Ativar'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={isBusy} onClick={() => openEdit(mission)}>
                      <PencilSimple size={14} className="mr-1" />
                      Editar
                    </Button>
                    {mission.status === 'rascunho' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        disabled={isBusy}
                        onClick={() => handleDelete(mission)}
                      >
                        <Trash size={14} />
                      </Button>
                    )}
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar missão' : 'Nova missão'}</DialogTitle>
            <DialogDescription>
              A missão é um capítulo da campanha. Rascunhos ficam visíveis só para você até serem ativados.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="mission-title">
                Título
              </label>
              <Input
                id="mission-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: A fuga do laboratório"
                maxLength={200}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="mission-desc">
                Descrição (opcional)
              </label>
              <Textarea
                id="mission-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Sinopse ou objetivo narrativo do capítulo"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {editing ? 'Salvar' : 'Criar missão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
